import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { TASK_AMENDMENT } from "constants/taskStates";
import { FEDERAL_INCOME_TAX_RETURN_KEYS } from "constants/taxYear";
import { Form, Formik } from "formik";
import { useToast } from "hooks/useToast";
import React, { FC } from "react";
import { useAmendFilingMutation } from "store/apis/task";

type AmendFilingConfirmationProps = {
  show: boolean;
  closeModal: () => void;
  taskId: string;
  groupId: string;
  baseTaskKey?: string;
};

type AmendFilingValues = {
  raise_invoice: boolean;
  amendment_reason: string;
};

const INITIAL_VALUES: AmendFilingValues = {
  raise_invoice: false,
  amendment_reason: "",
};

const buildPayload = (values: AmendFilingValues, isFederalReturn: boolean) => {
  if (!isFederalReturn) {
    return { next_state_type: TASK_AMENDMENT };
  }
  const amendmentReason = values.amendment_reason.trim();
  return {
    next_state_type: TASK_AMENDMENT,
    raise_invoice: values.raise_invoice,
    ...(amendmentReason && { amendment_reason: amendmentReason }),
  };
};

export const AmendFilingConfirmation: FC<AmendFilingConfirmationProps> = ({
  show,
  closeModal,
  taskId,
  groupId,
  baseTaskKey,
}) => {
  const [amendTask, { isLoading }] = useAmendFilingMutation();
  const { alertToast } = useToast();
  const isFederalReturn = Boolean(
    baseTaskKey && FEDERAL_INCOME_TAX_RETURN_KEYS.includes(baseTaskKey)
  );

  const amendFiling = async (values: AmendFilingValues) => {
    try {
      await amendTask({
        taskId,
        groupId,
        payload: buildPayload(values, isFederalReturn),
      }).unwrap();
    } catch (e: any) {
      alertToast({ message: e?.data?.error?.message });
    }
  };

  return (
    <Modal.Root open={show} onOpenChange={closeModal}>
      <Modal.Content>
        <Formik initialValues={INITIAL_VALUES} onSubmit={amendFiling}>
          {({ values, setFieldValue, submitForm }) => (
            <Form>
              <Modal.Header>
                <Modal.Title>Amend filing</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <div className="t-flex t-flex-col t-gap-4">
                  <div className="t-text-body-sm t-text-text-black">
                    Are you sure you want to amend this filing?
                    <div className="t-font-semibold">
                      You can't undo this action.
                    </div>
                  </div>
                  {isFederalReturn && (
                    <>
                      <Checkbox
                        name="raise_invoice"
                        label="Raise $200 invoice for this amendment"
                        checked={values.raise_invoice}
                        onChange={(e) =>
                          setFieldValue("raise_invoice", e.target.checked)
                        }
                      />
                      <TextArea
                        name="amendment_reason"
                        label="Reason for amendment"
                        placeholder="Optional"
                        block
                      />
                    </>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <div className="t-flex t-justify-end t-gap-3">
                  <Button type="button" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={submitForm}
                    disabled={isLoading}
                    customType="danger"
                    isLoading={isLoading}
                  >
                    Amend
                  </Button>
                </div>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
